import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Video, Calendar, MapPin, ArrowLeft, Download, Loader2, RefreshCw, Mic, Sparkles } from 'lucide-react';
import { Button } from '../ui/button';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const FieldReportsList = () => {
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);

  const token = localStorage.getItem('rooftrack_token');
  const headers = { Authorization: `Bearer ${token}` };

  const loadReports = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/field-reports?user_id=${user?.id}`, { headers });
      setReports(res.data || []);
    } catch (err) {
      console.error('Failed to load reports:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => { if (user?.id) loadReports(); }, [user?.id, loadReports]);

  const handleTranscribe = async (reportId) => {
    setProcessingId(reportId);
    try {
      await axios.post(`${API}/api/field-reports/${reportId}/transcribe`, {}, {
        params: { user_id: user.id },
        headers,
        timeout: 120000
      });
      toast.success('Transcription complete!');
      loadReports();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      if (detail.includes('still processing') || detail.includes('pending')) {
        toast.info('Transcription is processing in the background. Check back shortly.');
      } else {
        toast.error(`Transcription failed: ${detail}`);
      }
    } finally {
      setProcessingId(null);
    }
  };

  const handleGenerateReport = async (reportId) => {
    setProcessingId(reportId);
    try {
      const res = await axios.post(`${API}/api/field-reports/${reportId}/generate-report`, {}, {
        params: { user_id: user.id },
        headers,
        timeout: 120000
      });
      if (res.data?.report) {
        toast.success('AI report generated!');
      } else {
        toast.success('Report generation started');
      }
      loadReports();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Report generation failed');
    } finally {
      setProcessingId(null);
    }
  };

  const getStatusBadge = (report) => {
    if (report.ai_report) return { label: 'Report Ready', color: 'bg-green-100 text-green-700' };
    if (report.transcription && report.transcription_status === 'completed') return { label: 'Transcribed', color: 'bg-blue-100 text-blue-700' };
    if (report.transcription_status === 'processing') return { label: 'Processing', color: 'bg-yellow-100 text-yellow-700' };
    if (report.transcription_status === 'failed') return { label: 'Failed', color: 'bg-red-100 text-red-700' };
    if (report.audio_url) return { label: 'Video/Audio Only', color: 'bg-orange-100 text-orange-700' };
    return { label: 'Draft', color: 'bg-gray-100 text-gray-600' };
  };

  const getNextAction = (report) => {
    // Has report = done
    if (report.ai_report) return null;
    // Has transcription but no report = generate report
    if (report.transcription && report.transcription_status === 'completed') return 'generate';
    // Has audio but no transcription = transcribe first
    if (report.audio_url && (!report.transcription || report.transcription_status === 'failed' || report.transcription_status === 'pending')) return 'transcribe';
    // Processing = wait
    if (report.transcription_status === 'processing') return 'wait';
    return null;
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto p-6" data-testid="field-reports-list">
      <div className="flex items-center justify-between mb-6">
        <div>
          <Link to="/dashboard" className="text-sm text-gray-500 hover:text-orange-600 flex items-center gap-1 mb-1">
            <ArrowLeft className="w-3 h-3" /> Dashboard
          </Link>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FileText className="w-6 h-6 text-orange-600" />
            Field Reports
          </h1>
          <p className="text-sm text-gray-500 mt-1">{reports.length} saved reports</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => { setLoading(true); loadReports(); }}>
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
          <Link to="/dashboard/field-cam">
            <Button className="bg-orange-600 hover:bg-orange-700">
              <Video className="w-4 h-4 mr-2" /> New FieldCam
            </Button>
          </Link>
        </div>
      </div>

      {reports.length === 0 ? (
        <div className="text-center py-16 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
          <Video className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No field reports yet</p>
          <p className="text-sm text-gray-400 mt-1">Use FieldCam to record an inspection and generate an AI report</p>
        </div>
      ) : (
        <div className="space-y-3">
          {reports.map(report => {
            const status = getStatusBadge(report);
            const nextAction = getNextAction(report);
            const isProcessing = processingId === report.id;
            
            return (
              <div key={report.id} className="bg-white rounded-lg border shadow-sm hover:shadow-md transition-shadow p-4" data-testid={`report-${report.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-gray-900">
                        {report.property_address || 'Untitled Report'}
                      </h3>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${status.color}`}>
                        {status.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      {report.property_address && (
                        <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {report.property_address}</span>
                      )}
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {new Date(report.created_at).toLocaleDateString()}
                      </span>
                      {report.audio_url && (
                        <span className="flex items-center gap-1"><Video className="w-3 h-3" /> Recording attached</span>
                      )}
                    </div>
                    {report.transcription && (
                      <p className="text-sm text-gray-600 mt-2 line-clamp-2">{report.transcription}</p>
                    )}
                  </div>
                  
                  <div className="flex gap-2 ml-4 flex-shrink-0">
                    {/* Next action button based on report state */}
                    {nextAction === 'transcribe' && (
                      <Button 
                        size="sm" 
                        onClick={() => handleTranscribe(report.id)}
                        disabled={isProcessing}
                        className="bg-blue-600 hover:bg-blue-700"
                        data-testid={`transcribe-btn-${report.id}`}
                      >
                        {isProcessing ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Mic className="w-3 h-3 mr-1" />}
                        Transcribe
                      </Button>
                    )}
                    
                    {nextAction === 'generate' && (
                      <Button 
                        size="sm" 
                        onClick={() => handleGenerateReport(report.id)}
                        disabled={isProcessing}
                        className="bg-green-600 hover:bg-green-700"
                        data-testid={`generate-btn-${report.id}`}
                      >
                        {isProcessing ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
                        Generate Report
                      </Button>
                    )}
                    
                    {nextAction === 'wait' && (
                      <Button size="sm" variant="outline" disabled>
                        <Loader2 className="w-3 h-3 mr-1 animate-spin" /> Processing...
                      </Button>
                    )}
                    
                    {report.ai_report && (
                      <Link to={`/dashboard/field-cam?reportId=${report.id}`}>
                        <Button size="sm" variant="outline">View Report</Button>
                      </Link>
                    )}
                    
                    {report.audio_url && (
                      <a href={report.audio_url} target="_blank" rel="noopener noreferrer">
                        <Button size="sm" variant="outline"><Download className="w-3 h-3 mr-1" /> Media</Button>
                      </a>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FieldReportsList;
